import {
  type KeyOfType,
  getValueFromKeyOfType,
} from '../../../types/KeyOfType';
import { createSelectFilter } from '../../filters';
import { createDefaultFilter } from '../../filters/createDefaultFilter';
import type {
  TableColumnFormatLink,
  TableLinkColumnConfig,
} from '../../links/index';
import type { TableColumnBase } from '../TableColumnBase';
import {
  type TableDataColumnConfig,
  pickTableColumnConfig,
} from '../TableColumnConfig';
import { mergeFieldDependencies } from './helpers/mergeFieldDependencies';

export type EnumColumnOptions = { [key: string]: string };

export type EnumColumnOptionKeyTypes = 'string' | 'number';

export type TableColumnFormatEnum<TRow, TDataType> = {
  type: 'enum';
  field: KeyOfType<TRow, TDataType | null>;
  options: EnumColumnOptions;
  multiSelectFilter: boolean;
} & TableColumnFormatLink<TRow>;

type EnumColumnConfig<TRow, TDataType> = {
  options: EnumColumnOptions;
  multiSelectFilter?: boolean;
  optionKeyType?: EnumColumnOptionKeyTypes;
} & TableDataColumnConfig<TRow, TDataType> &
  TableLinkColumnConfig<TRow>;

export const createEnumColumn: <TRow, TDataType>(
  config: EnumColumnConfig<TRow, TDataType | null> & { array?: false },
) => TableColumnBase<TRow, TableColumnFormatEnum<TRow, TDataType>> = <
  TRow,
  TDataType,
>(
  config: EnumColumnConfig<TRow, TDataType | null>,
): TableColumnBase<TRow, TableColumnFormatEnum<TRow, TDataType>> => {
  // Guard conditions to provide better guidance and stricter checking.
  // 1. If optionKeyType is 'number', ensure ALL option keys are valid, unique numbers.
  // 2. If no explicit optionKeyType is provided, warn if all option keys are numeric, as we assume strings by default.
  // 3. If optionKeyType is 'string', ensure all option keys are strings (though in JS/TS object keys are always strings, the intent is to avoid numbers as keys).

  // 1. Reject if optionKeyType is 'number' but any key isn't a valid integer string, or duplicate numeric keys (to avoid odd JS coercion issues).
  if (config.optionKeyType === 'number') {
    const numericKeys = Object.keys(config.options).map(Number);
    if (
      numericKeys.some((n) => !Number.isFinite(n)) ||
      numericKeys.length !== new Set(numericKeys).size
    ) {
      throw new Error(
        `Table column ${config.key}: optionKeyType is 'number', but some keys are not valid unique numbers.`,
      );
    }
  }

  // 2. Warn if all keys are numbers but optionKeyType is not specified (may indicate a config mistake).
  if (
    config.optionKeyType === undefined &&
    Object.keys(config.options).length > 0 &&
    Object.keys(config.options).every((key) => !Number.isNaN(Number(key)))
  ) {
    throw new Error(
      `Table column ${config.key}: All option keys are numeric. You should set optionKeyType to 'number' (if intended), or rename your keys to strings.`,
    );
  }

  // 3. If optionKeyType is 'string', we could check for non-numeric keys, but in JS all keys are strings.
  // As an extra check, warn if all keys are actually numeric strings, which may indicate user confusion.
  if (
    config.optionKeyType === 'string' &&
    Object.keys(config.options).length > 0 &&
    Object.keys(config.options).every((key) => !Number.isNaN(Number(key)))
  ) {
    throw new Error(
      `Table column ${config.key}: optionKeyType is 'string', but all option keys are numeric strings. Did you mean to use optionKeyType 'number'?`,
    );
  }

  return {
    ...pickTableColumnConfig(config),
    format: {
      type: 'enum',
      field: config.key,
      options: config.options,
      createLinkProps: config.link?.createLinkProps,
      multiSelectFilter: config.multiSelectFilter ?? false,
    },
    csvStringDependencies: [config.key],
    webDependencies: mergeFieldDependencies(
      config.key,
      config.link?.dependencies,
    ),
    toCsvString: (row) => {
      const val = getValueFromKeyOfType(row, config.key);

      return config.options[`${val}`] ?? `${val ?? ''}`;
    },
    filter: createDefaultFilter(
      config.filter,
      createSelectFilter(
        Object.entries(config.options).map(([value, label]) => ({
          value,
          label,
        })),
        config.multiSelectFilter,
        config.optionKeyType,
      ),
    ),
  };
};
